import { Div, Text } from "atomize"
import { theme } from "../settings/config"
import { CARD_TEXT } from "./lessonCardText"
import { AnimatePresence, motion } from "framer-motion"
import { useContext } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faRightLeft, faUserSlash } from "@fortawesome/free-solid-svg-icons"
import { sharedDataContext } from "../providers/sharedData.provider"
import {
	getLessonColor,
	getNamedPartecipants,
	getPaymentMethodName,
	paymentCoveredElsewhere,
} from "../utils/func.utils"
import { regularPartecipants, substituteOf } from "../utils/substitutes.utils"
import { paidAtSchool } from "../utils/billing.utils"
import { isCancelledLesson } from "../utils/cancellation.utils"

// `sharedLayout` spento: i nomi si spostano solo quando la scheda si apre o
// si chiude, non quando la scheda viene rifatta altrove (il passaggio tra
// giorno e settimana sul telefono, vedi MorphingLesson). Con lo stesso
// layoutId framer-motion li farebbe volare da dov'erano prima, sopra
// all'orario.
function LessonBody({ lessonData, isOpen, isLayoutOpen, modalView, sharedLayout = true }) {
	const { getPaymentMethod, getLessonType } = useContext(sharedDataContext)

	if (lessonData.partecipants.length === 0) return null

	// I posti ancora senza nome — un SAT fissato prima di sapere chi viene —
	// non si elencano uno per uno: si contano.
	const regulars = regularPartecipants(lessonData.partecipants)
	const namedRegulars = getNamedPartecipants(regulars)
	const placeholdersCount = regulars.length - namedRegulars.length

	// Un nome per posto: il sostituto sta al posto di chi manca, e chi manca
	// non si elenca. Un CAD da quattro resta di quattro nomi — chi è in campo
	// — e chi è assente si vede nelle presenze e, aperta la lezione, accanto
	// a chi l'ha sostituito.
	//
	// Chi manca e non ha ancora un sostituto invece resta, in rosso: lo si
	// segna assente anche giorni prima, e dalla lezione chiusa deve saltare
	// all'occhio che c'è un posto da riempire.
	//
	// In una lezione annullata non manca nessuno: non si è fatta. Si
	// elencano tutti com'erano, e accanto agli abbonati c'è il recupero.
	const isCancelled = isCancelledLesson(lessonData)
	const partecipants = namedRegulars.map(player => {
		if (isCancelled) return { ...player, isCancelled: true }
		if (player.isPresent !== false) return player
		const substitute = substituteOf(lessonData.partecipants, player)
		return substitute ? { ...substitute, replaces: player } : { ...player, isAbsent: true }
	})
	const swapColor = getLessonColor(getLessonType(lessonData.lessonType), true)
	const placeholdersText =
		placeholdersCount === 1 ? "1 posto da segnare" : `${placeholdersCount} posti da segnare`

	const formatPayment = player => {
		if (player.isCancelled)
			return player.paymentMethod === "subscription" ? " \u00B7 da recuperare" : ""
		if (player.isAbsent) return " \u00B7 assente, senza sostituto"
		const replaces = player.replaces
			? ` \u00B7 al posto di ${player.replaces.playerName.trim()}`
			: ""
		// chi ha pagato il circolo un metodo non ce l'ha, e non va chiesto
		const payment = paidAtSchool(player, lessonData)
			? ` \u00B7 al circolo: ${player.paymentAmount}€`
			: player.paymentMethod
				? ` \u00B7 ${getPaymentMethodName(getPaymentMethod, player.paymentMethod)}${
						!paymentCoveredElsewhere(player.paymentMethod)
							? `: ${player.paymentAmount}€`
							: ""
					}`
				: ` \u00B7 come paga i ${player.paymentAmount}€?`
		return replaces + payment
	}

	const pill = {
		display: "inline-flex",
		alignItems: "center",
		gap: "0.25rem",
		borderRadius: "10px",
		padding: "0 0.5rem 0 0.375rem",
	}

	// il sostituto si riconosce dalla pastiglia con lo scambio, chi manca
	// ancora senza sostituto da quella rossa
	const playerName = player => {
		if (player.replaces)
			return (
				<span style={{ ...pill, background: theme.colors.surface }}>
					<FontAwesomeIcon icon={faRightLeft} color={swapColor} size="xs" />
					{player.playerName.trim()}
				</span>
			)
		if (player.isAbsent)
			return (
				<span
					style={{ ...pill, background: theme.colors.red_light, color: theme.colors.red }}>
					<FontAwesomeIcon icon={faUserSlash} color={theme.colors.red} size="xs" />
					{player.playerName.trim()}
				</span>
			)
		return player.playerName.trim()
	}

	return (
		<Div m={{ t: "0.35rem", b: "0.6rem" }}>
			{partecipants.length === 0 ? null : partecipants.length === 1 ? (
				<Div data-morph="names" d="inline-flex" align="baseline" style={{ gap: "0.25rem" }}>
					<Text
						tag="span"
						textSize="subheader"
						textWeight="bold"
						textColor={theme.colors.gunmetal}
						style={{ fontSize: CARD_TEXT.name, lineHeight: CARD_TEXT.nameLine }}>
						{playerName(partecipants[0])}
					</Text>
					<Text tag="span" textSize="caption" style={{ fontSize: CARD_TEXT.aside }}>
						{formatPayment(partecipants[0])}
					</Text>
				</Div>
			) : (
				<div
					data-morph="names"
					style={{
						display: "flex",
						flexDirection: isLayoutOpen ? "column" : "row",
						flexWrap: isLayoutOpen ? "nowrap" : "wrap",
						gap: "0.25rem",
					}}>
					{partecipants.map((player, i) => (
						<motion.div
							// il posto nell'elenco, non solo chi è: due allievi
							// con lo stesso ID — succede — davano due chiavi
							// uguali a React e due layoutId uguali a
							// framer-motion, che li faceva sparire lasciando il
							// buco al loro posto
							key={`${player.playerID || "player"}-${i}`}
							layoutId={
								modalView || !sharedLayout
									? undefined
									: `${lessonData.lessonID}-player-${player.playerID || "player"}-${i}`
							}
							layout="position"
							style={{
								display: "flex",
								alignItems: "baseline",
								gap: "0.25rem",
								margin: 0,
							}}
							transition={{
								layout: {
									type: "spring",
									stiffness: 350,
									damping: 35,
								},
							}}>
							<Text
								tag="span"
								textWeight="bold"
								textSize="subheader"
								textColor={theme.colors.gunmetal}
								style={{
									fontSize: CARD_TEXT.name,
									lineHeight: CARD_TEXT.nameLine,
									whiteSpace: "nowrap",
								}}>
								{playerName(player)}
								{!isLayoutOpen &&
									player !== partecipants.slice(-1)[0] &&
									","}
							</Text>
							<AnimatePresence>
								{isOpen && (
									<motion.span
										initial={{ opacity: 0, x: -8 }}
										animate={{ opacity: 1, x: 0 }}
										exit={{ opacity: 0, maxWidth: 0 }}
										transition={{
											opacity: { duration: 0.12, delay: i * 0.05 + 0.1 },
											x: { duration: 0.12, delay: i * 0.05 + 0.1 },
											maxWidth: { duration: 0 },
										}}
										style={{
											whiteSpace: "nowrap",
											lineHeight: CARD_TEXT.nameLine,
											overflow: "hidden",
											display: "inline-flex",
											alignItems: "baseline",
											maxWidth: "500px",
										}}>
										<Text tag="span" textSize="caption" style={{ fontSize: CARD_TEXT.aside }}>
											{formatPayment(player)}
										</Text>
									</motion.span>
								)}
							</AnimatePresence>
						</motion.div>
					))}
				</div>
			)}
			{placeholdersCount > 0 && (
				<Text textSize="caption" textColor={theme.colors.text_light}>
					{partecipants.length > 0 ? `+ ${placeholdersText}` : placeholdersText}
				</Text>
			)}
		</Div>
	)
}

export default LessonBody
